import { ReconciliationSupplierDetail } from 'apis/reconciliation';
import { Button, HStack, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from 'design-system';
import { formatAed, formatVariance, varianceColor } from 'pages/reconciliation/utils/format';
import { ReactNode } from 'react';

export interface IGmvByRestaurantTable {
  restaurants: ReconciliationSupplierDetail['restaurants'];
  totals: { platformGmv: number; soaGmv: number | null; variance: number | null; commission: number };
  /** Months closed under the old (pre-per-order) revenue model have no commission breakdown —
   * their frozen per-restaurant rows all carry `commission: 0`, which would render as data loss
   * next to a non-zero Revenue. Gate the Commission column on this instead of showing zeros. */
  showCommission: boolean;
  /** Optional right-aligned control on the section header line (e.g. the expanded row's "Edit SOA" button). */
  headerAction?: ReactNode;
  /** When provided, adds a per-row "View orders" action column (opens the orders modal). */
  onViewOrders?: (restaurant: { restaurantId: number; restaurantName: string }) => void;
}

export const GmvByRestaurantTable = ({
  restaurants,
  totals,
  showCommission,
  headerAction,
  onViewOrders,
}: IGmvByRestaurantTable) => {
  // Credit, when entered, is the effective SOA figure on the BE — variance/discrepancy/invoice
  // base use `effectiveSoa = soaCredit ?? soaGmv` per restaurant ("Paid wins when entered");
  // debit stays the displayed SOA GMV column. The row/summary aggregates still don't carry a
  // credit total, so it's derived here from the loaded breakdown. Kept `null` (rendered as '—')
  // when nothing has been entered yet, same convention as soaGmv.
  const totalCredit = restaurants.some((r) => r.soaCredit !== null)
    ? restaurants.reduce((sum, r) => sum + (r.soaCredit ?? 0), 0)
    : null;

  return (
    <Stack spacing='xs'>
      <HStack justify='space-between' align='center' minH='24px'>
        <Text fontSize='xs' fontWeight='semibold' color='gray.500' textTransform='uppercase' letterSpacing='0.4px'>
          GMV vs SOA by restaurant
        </Text>
        {headerAction}
      </HStack>

      {/* Own TableContainer so this 5-or-6-column breakdown (Commission only shows up when
          showCommission is true) scrolls internally on its own when the section is genuinely
          too narrow — but with whiteSpace='normal': Chakra's TableContainer sets
          `white-space: nowrap` on itself, which would re-poison this subtree (overriding
          supplier-row.tsx's whiteSpace='normal' reset, since this is the nearer ancestor) and
          make the table's minimum width equal to the longest UNWRAPPED restaurant name plus all
          numeric columns — forcing an internal scrollbar even when the section has ample room.
          With normal whitespace, restaurant names wrap and the auto-layout table (w='100%') fits
          the container; the numeric cells opt back into nowrap individually so amounts never
          break mid-number. sx trims the size='sm' cell padding (16px/side) to 10px — dense
          columns of figures, keeps the no-scroll fit tight — and zeroes the first column's left
          pad so rows align with the section heading above. */}
      <TableContainer overflowX='auto' whiteSpace='normal'>
        <Table
          size='sm'
          w='100%'
          sx={{
            'th, td': { px: '10px' },
            'th:first-of-type, td:first-of-type': { pl: 0 },
          }}>
          <Thead>
            <Tr>
              <Th fontSize='xs' fontWeight='semibold' color='gray.400'>
                Restaurant
              </Th>
              <Th isNumeric fontSize='xs' fontWeight='semibold' color='gray.400' whiteSpace='nowrap'>
                Platform GMV
              </Th>
              {showCommission && (
                <Th isNumeric fontSize='xs' fontWeight='semibold' color='gray.400' whiteSpace='nowrap'>
                  Commission
                </Th>
              )}
              <Th isNumeric fontSize='xs' fontWeight='semibold' color='gray.400' whiteSpace='nowrap'>
                SOA GMV
              </Th>
              <Th isNumeric fontSize='xs' fontWeight='semibold' color='gray.400' whiteSpace='nowrap'>
                Paid (SOA)
              </Th>
              <Th isNumeric fontSize='xs' fontWeight='semibold' color='gray.400' whiteSpace='nowrap'>
                Variance
              </Th>
              {onViewOrders && <Th w='84px' />}
            </Tr>
          </Thead>
          <Tbody>
            {restaurants.length ? (
              restaurants.map((restaurant) => (
                <Tr key={restaurant.restaurantId}>
                  {/* wordBreak guards a single pathological unbroken token (no spaces to wrap at)
                      from re-inflating the column's min-content width. */}
                  <Td wordBreak='break-word'>{restaurant.restaurantName}</Td>
                  <Td isNumeric whiteSpace='nowrap'>
                    {formatAed(restaurant.platformGmv)}
                  </Td>
                  {showCommission && (
                    <Td isNumeric whiteSpace='nowrap'>
                      {formatAed(restaurant.commission)}
                    </Td>
                  )}
                  <Td isNumeric whiteSpace='nowrap' color={restaurant.soaGmv === null ? 'gray.400' : undefined}>
                    {restaurant.soaGmv === null ? 'Not entered' : formatAed(restaurant.soaGmv)}
                  </Td>
                  <Td isNumeric whiteSpace='nowrap' color='gray.400'>
                    {restaurant.soaCredit === null ? '—' : formatAed(restaurant.soaCredit)}
                  </Td>
                  <Td isNumeric whiteSpace='nowrap' color={varianceColor(restaurant.variance)} fontWeight='semibold'>
                    {formatVariance(restaurant.variance)}
                  </Td>
                  {onViewOrders && (
                    <Td whiteSpace='nowrap'>
                      <Button
                        size='xs'
                        variant='link'
                        onClick={() =>
                          onViewOrders({
                            restaurantId: restaurant.restaurantId,
                            restaurantName: restaurant.restaurantName,
                          })
                        }>
                        View orders
                      </Button>
                    </Td>
                  )}
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={(showCommission ? 6 : 5) + (onViewOrders ? 1 : 0)} textAlign='center' color='gray.500'>
                  No restaurant breakdown available
                </Td>
              </Tr>
            )}
          </Tbody>
          <Tbody>
            <Tr fontWeight='semibold' borderTop='1px solid' borderColor='gray.200'>
              <Td>Total</Td>
              <Td isNumeric whiteSpace='nowrap'>
                {formatAed(totals.platformGmv)}
              </Td>
              {showCommission && (
                <Td isNumeric whiteSpace='nowrap'>
                  {formatAed(totals.commission)}
                </Td>
              )}
              <Td isNumeric whiteSpace='nowrap' color={totals.soaGmv === null ? 'gray.400' : undefined}>
                {totals.soaGmv === null ? 'Not entered' : formatAed(totals.soaGmv)}
              </Td>
              <Td isNumeric whiteSpace='nowrap' color='gray.400' fontWeight='normal'>
                {totalCredit === null ? '—' : formatAed(totalCredit)}
              </Td>
              <Td isNumeric whiteSpace='nowrap' color={varianceColor(totals.variance)}>
                {formatVariance(totals.variance)}
              </Td>
              {onViewOrders && <Td />}
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>
    </Stack>
  );
};
